import type {
  CreateArtifactResponse,
  UpdateArtifactResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/artifact_service_pb';
import type { Experiment } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_pb';
import type {
  BatchDeleteExperimentResponse,
  CreateExperimentResponse,
  DeleteExperimentRequest,
  GetExperimentResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_service_pb';
import type { ListProjectsResponse } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/project_service_pb';
import type { RegisteredModel } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_pb';
import type {
  GetRegisteredModelResponse,
  ListRegisteredModelsResponse,
  UpdateRegisteredModelResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_service_pb';
import type { RegisteredModelVersion } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_pb';
import type {
  CreateModelVersionResponse,
  DeleteModelVersionResponse,
  ListModelVersionsForModelResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_service_pb';
import type { ListTagsResponse } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/tag_service_pb.js';
import type { PlainMessage } from '@bufbuild/protobuf';
import {
  type QueryFilters,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  useMutation,
  type UseMutationOptions,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { useCloudPlatformDiscovery } from '../utils/hooks';
import {
  batchDeleteExperiment,
  type BatchDeleteExperimentRequestPlain,
  batchDeleteModelVersion,
  batchDeleteRegisteredModel,
  createArtifact,
  type CreateArtifactRequestPlain,
  createExperiment,
  type CreateExperimentRequestPlain,
  createModelVersion,
  type CreateModelVersionRequestPlain,
  deleteModelVersion,
  deleteRegisteredModel,
  getExperiment,
  getRegisteredModel,
  listModelVersionsForModel,
  ListModelVersionsForModelRequestPlain,
  listProjects,
  type ListProjectsRequestPlain,
  listRegisteredModels,
  type ListRegisteredModelsRequestPlain,
  type ListTagRequestPlain,
  listTags,
  updateArtifact,
  type UpdateArtifactRequestPlain,
  updateRegisteredModel,
  type UpdateRegisteredModelRequestPlain,
} from './mlops.storage.functions';

export const cacheKeys = {
  experimentOne: (projectId: string, experimentId: string) => [
    `mlops`,
    `storage`,
    projectId,
    `experiment`,
    `one`,
    experimentId,
  ],
  projectsAll: (params?: Pick<ListProjectsRequestPlain, `filter` | `sorting`>) => {
    const baseKey: QueryKey = [`mlops`, `storage`, `projects`, `all`];
    if (!params) return baseKey;
    const { filter, sorting } = params;
    return baseKey.concat({ filter, sorting });
  },
  registeredModelsAll: (projectId: string, params?: Pick<ListRegisteredModelsRequestPlain, `filter` | `sorting`>) => {
    const baseKey: QueryKey = [`mlops`, `storage`, projectId, `registeredModels`, `all`];
    if (!params) return baseKey;
    const { filter, sorting } = params;
    return baseKey.concat({ filter, sorting });
  },
  registeredModelOne: (projectId: string, modelId: string) => [
    `mlops`,
    `storage`,
    projectId,
    `registeredModels`,
    `one`,
    modelId,
  ],
  registeredModelVersionsAll: (
    projectId: string,
    modelId: string,
    params?: Pick<ListModelVersionsForModelRequestPlain, `filter` | `sorting`>
  ) => {
    const baseKey: QueryKey = [...cacheKeys.registeredModelOne(projectId, modelId), `versions`, `all`];
    if (!params) return baseKey;
    const { filter, sorting } = params;
    return baseKey.concat({ filter, sorting });
  },
  tagsAll: (projectId: string, params?: Pick<ListTagRequestPlain, `filter` | `sorting`>) => {
    const baseKey: QueryKey = [`mlops`, `storage`, `tags`, `all`, projectId];
    if (!params) return baseKey;
    const { filter, sorting } = params;
    return baseKey.concat({ filter, sorting });
  },
};

// Experiments

export const useMutationDeleteExperiments = (
  options?: Omit<
    UseMutationOptions<BatchDeleteExperimentResponse, unknown, BatchDeleteExperimentRequestPlain>,
    `mutationFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: BatchDeleteExperimentRequestPlain) => {
      const response = await batchDeleteExperiment(mlopsApiUrl, request);
      // start with `-` (wildcard) project to invalidate admin listing of all registered models, if cached
      const invalidateProjects = new Set<string>([`-`]);
      for (const experiment of request.experimentRequest) {
        invalidateProjects.add(experiment.projectId);
      }
      invalidateProjects.forEach((projectId) => {
        const queryFilter: QueryFilters = {
          queryKey: [`mlops`, `storage`, projectId],
          exact: false,
        };
        queryClient.invalidateQueries(queryFilter);
      });

      return response;
    },
  });
};

export const useMutationCreateExperiment = (
  options?: Omit<UseMutationOptions<CreateExperimentResponse, unknown, CreateExperimentRequestPlain>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: CreateExperimentRequestPlain) => {
      const response = await createExperiment(mlopsApiUrl, request);
      // Invalidate experiments cache
      if (request.projectId) {
        queryClient.invalidateQueries({
          queryKey: [`mlops`, `storage`, request.projectId],
          exact: false,
        });
      }
      return response;
    },
  });
};

export const getExperimentQueryOptions = <TData = GetExperimentResponse>(
  mlopsApiUrl: string,
  projectId: string,
  experimentId: string
): UseQueryOptions<GetExperimentResponse, unknown, TData> => {
  return {
    queryFn: ({ signal }) => getExperiment(mlopsApiUrl, { id: experimentId }, { signal }),
    queryKey: cacheKeys.experimentOne(projectId, experimentId),
  };
};

// Artifacts

export const useMutationCreateArtifact = (
  options?: Omit<UseMutationOptions<CreateArtifactResponse, unknown, CreateArtifactRequestPlain>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  return useMutation({
    ...options,
    mutationFn: async (request: CreateArtifactRequestPlain) => {
      return await createArtifact(mlopsApiUrl, request);
    },
  });
};

export const useMutationUpdateArtifact = (
  options?: Omit<UseMutationOptions<UpdateArtifactResponse, unknown, UpdateArtifactRequestPlain>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  return useMutation({
    ...options,
    mutationFn: async (request: UpdateArtifactRequestPlain) => {
      return await updateArtifact(mlopsApiUrl, request);
    },
  });
};

// Projects

export const useQueryListProjects = <TData = ListProjectsResponse>(
  request: ListProjectsRequestPlain,
  options?: Omit<
    UseInfiniteQueryOptions<ListProjectsResponse, unknown, TData>,
    `getNextPageParam` | `queryFn` | `queryKey`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  return useInfiniteQuery({
    ...options,
    getNextPageParam: (lastPage) => lastPage.paging?.nextPageToken || undefined,
    queryFn: ({ pageParam, signal }) =>
      listProjects(
        mlopsApiUrl,
        { ...request, paging: { pageSize: request.paging?.pageSize as any, pageToken: pageParam } },
        { signal }
      ),
    queryKey: cacheKeys.projectsAll(request),
  });
};

// Registered Models

export const useMutationBatchDeleteRegisteredModelsAndModelVersions = (
  options?: Omit<
    UseMutationOptions<
      void,
      unknown,
      {
        models: Array<Pick<RegisteredModel, `id` | `projectId`>>;
        versionIds: string[];
      }
    >,
    `mutationFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async ({ models, versionIds }) => {
      const modelsVersions = await Promise.all(
        models.map(({ id, projectId }) =>
          queryClient.fetchQuery(
            getRegisteredModelVersionsQueryOptions(mlopsApiUrl, projectId, { registeredModelId: id })
          )
        )
      );
      const experimentRequest: PlainMessage<DeleteExperimentRequest>[] = models.flatMap(({ projectId }, idx) => {
        const modelVersions = modelsVersions[idx].modelVersions;
        return modelVersions.map((version) => ({ id: version.experimentId, projectId }));
      });
      try {
        if (experimentRequest.length) {
          const batchDeleteExperimentResponse = await batchDeleteExperiment(mlopsApiUrl, { experimentRequest });
          const failedResponses = batchDeleteExperimentResponse.experimentDeleteResponse.filter((r) => !r.status);
          if (failedResponses.length) {
            throw new Error(
              `Failed to delete experiments for registered models: ${failedResponses.map((r) => r.message).join(`, `)}`
            );
          }
        }
        if (versionIds.length) {
          const batchDeleteVersionsResponse = await batchDeleteModelVersion(mlopsApiUrl, { ids: versionIds });
          const failedResponses = batchDeleteVersionsResponse.deleteModelVersionResponse.filter((r) => !r.status);
          if (failedResponses.length) {
            throw new Error(`Failed to delete model versions: ${failedResponses.map((r) => r.message).join(`, `)}`);
          }
        }
        if (models.length) {
          const batchDeleteModelResponse = await batchDeleteRegisteredModel(mlopsApiUrl, {
            modelIds: models.map((m) => m.id),
          });
          const failedResponses = batchDeleteModelResponse.deleteRegisteredModelResponse.filter((r) => !r.status);
          if (failedResponses.length) {
            throw new Error(`Failed to delete registered models: ${failedResponses.map((r) => r.message).join(`, `)}`);
          }
        }
      } catch (error) {
        throw error;
      } finally {
        // Invalidate all cached data for the whole mlops storage. We can be more granular, but that would be too complicated 🥱
        // Invalidate in the `finally` block since the _batch_ requests can fail only partially, so some data may be deleted even in case of an error.
        queryClient.invalidateQueries({
          queryKey: [`mlops`, `storage`],
          exact: false,
        });
      }
    },
  });
};

export const useMutationDeleteRegisteredModelAndExperiments = (
  options?: Omit<
    UseMutationOptions<
      void,
      unknown,
      {
        projectId: string;
        registeredModelId: string;
      }
    >,
    `mutationFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async ({ registeredModelId, projectId }) => {
      const { modelVersions } = await queryClient.fetchQuery(
        getRegisteredModelVersionsQueryOptions(mlopsApiUrl, projectId, { registeredModelId })
      );

      if (modelVersions.length) {
        const batchDeleteExperimentResponse = await batchDeleteExperiment(mlopsApiUrl, {
          experimentRequest: modelVersions.map((v) => ({ id: v.experimentId, projectId })),
        });
        queryClient.invalidateQueries({
          queryKey: cacheKeys.registeredModelVersionsAll(projectId, registeredModelId),
          exact: false,
        });
        const failedResponses = batchDeleteExperimentResponse.experimentDeleteResponse.filter((r) => !r.status);
        if (failedResponses.length) {
          throw new Error(
            `Failed to delete experiments for registered model ${registeredModelId}: ${failedResponses
              .map((r) => r.message)
              .join(`, `)}`
          );
        }
      }

      await deleteRegisteredModel(mlopsApiUrl, { modelId: registeredModelId });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.registeredModelsAll(projectId),
        exact: false,
      });
      // invalidate also the admin listing of all registered models, if cached
      queryClient.invalidateQueries({
        queryKey: cacheKeys.registeredModelsAll(`-`),
        exact: false,
      });
    },
  });
};

export const useQueryRegisteredModels = <TData = ListRegisteredModelsResponse>(
  request: ListRegisteredModelsRequestPlain,
  options?: Omit<
    UseInfiniteQueryOptions<ListRegisteredModelsResponse, unknown, TData>,
    `getNextPageParam` | `queryFn` | `queryKey`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  return useInfiniteQuery({
    ...options,
    getNextPageParam: (lastPage) => lastPage.paging?.nextPageToken || undefined,
    queryFn: ({ pageParam, signal }) =>
      listRegisteredModels(
        mlopsApiUrl,
        { ...request, paging: { pageSize: request.paging?.pageSize || 20, pageToken: pageParam } },
        { signal }
      ),
    // if projectId is empty, then the listing reads registered models across all projects (admin only). in that case,
    // the cache key uses `-` as a wildcard projectId.
    queryKey: cacheKeys.registeredModelsAll(request.projectId || `-`, request),
  });
};

export const getRegisteredModelQueryOptions = <TData = GetRegisteredModelResponse>(
  mlopsApiUrl: string,
  projectId: string,
  modelId: string
): UseQueryOptions<GetRegisteredModelResponse, unknown, TData> => {
  return {
    queryFn: ({ signal }) => getRegisteredModel(mlopsApiUrl, { modelId }, { signal }),
    queryKey: cacheKeys.registeredModelOne(projectId, modelId),
  };
};

export const useMutationUpdateRegisteredModel = (
  options?: Omit<
    UseMutationOptions<
      UpdateRegisteredModelResponse,
      unknown,
      UpdateRegisteredModelRequestPlain & { projectId: string; modelId: string }
    >,
    `mutationFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async ({
      projectId,
      modelId,
      ...request
    }: UpdateRegisteredModelRequestPlain & { projectId: string; modelId: string }) => {
      const response = await updateRegisteredModel(mlopsApiUrl, request);
      queryClient.invalidateQueries({
        queryKey: cacheKeys.registeredModelOne(projectId, modelId),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.registeredModelsAll(projectId),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.registeredModelsAll(`-`),
        exact: false,
      });
      return response;
    },
  });
};

// Registered Model Versions

export const useQueryModelVersionsForModel = <TData = ListModelVersionsForModelResponse>(
  request: ListModelVersionsForModelRequestPlain & { projectId: string },
  options?: Omit<
    UseInfiniteQueryOptions<ListModelVersionsForModelResponse, unknown, TData>,
    `getNextPageParam` | `queryFn` | `queryKey`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const { projectId, ...requestWithoutProjectId } = request;
  return useInfiniteQuery({
    ...options,
    getNextPageParam: (lastPage) => lastPage.paging?.nextPageToken || undefined,
    queryFn: ({ pageParam, signal }) =>
      listModelVersionsForModel(
        mlopsApiUrl,
        { ...requestWithoutProjectId, paging: { pageSize: request.paging?.pageSize as any, pageToken: pageParam } },
        { signal }
      ),
    queryKey: cacheKeys.registeredModelVersionsAll(projectId, request.registeredModelId, requestWithoutProjectId),
  });
};

export const useMutationCreateModelVersion = (
  options?: Omit<UseMutationOptions<CreateModelVersionResponse, unknown, CreateModelVersionRequestPlain>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request) => {
      const response = await createModelVersion(mlopsApiUrl, request);
      // Invalidate registered model versions cache
      if (request.projectId && request.registeredModelVersion?.registeredModelId) {
        queryClient.invalidateQueries({
          queryKey: cacheKeys.registeredModelVersionsAll(
            request.projectId,
            request.registeredModelVersion.registeredModelId
          ),
          exact: false,
        });
      }
      return response;
    },
  });
};

export const useMutationDeleteModelVersion = (
  options?: Omit<
    UseMutationOptions<
      DeleteModelVersionResponse,
      unknown,
      {
        projectId: string;
        versionId: string;
      }
    >,
    `mutationFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async ({ projectId, versionId }) => {
      const response = await deleteModelVersion(mlopsApiUrl, { id: versionId });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.registeredModelVersionsAll(projectId, versionId),
        exact: false,
      });
      return response;
    },
  });
};

export const getRegisteredModelVersionsQueryOptions = <TData = ListModelVersionsForModelResponse>(
  mlopsApiUrl: string,
  projectId: string,
  request: ListModelVersionsForModelRequestPlain
): UseQueryOptions<ListModelVersionsForModelResponse, unknown, TData> => {
  return {
    queryFn: ({ signal }) => listModelVersionsForModel(mlopsApiUrl, request, { signal }),
    queryKey: cacheKeys.registeredModelVersionsAll(projectId, request.registeredModelId, request),
  };
};

export type RegisteredModelVersionWithExperiment = RegisteredModelVersion & {
  experiment?: Experiment;
};

export const useQueriesRegisteredModelsVersionsWithExperiments = (
  registeredModels: RegisteredModel[],
  modelVersionsQueryOptions?: Omit<
    UseQueryOptions<ListModelVersionsForModelResponse, unknown, ListModelVersionsForModelResponse>,
    `queryFn` | `queryKey`
  >
): {
  loading: boolean;
  registeredModelVersionsWithExperimentsByRegisteredModelId: Record<string, RegisteredModelVersionWithExperiment[]>;
} => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const projectIds: string[] = [];
  const modelVersionsQueries = registeredModels.map((registeredModel) => {
    const projectId = registeredModel.projectId!;
    projectIds.push(projectId);
    return {
      ...modelVersionsQueryOptions,
      ...getRegisteredModelVersionsQueryOptions(mlopsApiUrl, projectId, { registeredModelId: registeredModel.id }),
    };
  });
  const modelVersionsQueriesResults = useQueries({ queries: modelVersionsQueries });
  const allVersions = modelVersionsQueriesResults.flatMap((queryResult, idx) => {
    const projectId = projectIds[idx];
    return queryResult.data?.modelVersions?.map((version) => ({ projectId, version })) || [];
  });
  const experimentQueries = allVersions.map(({ projectId, version: { experimentId } }) =>
    getExperimentQueryOptions(mlopsApiUrl, projectId, experimentId)
  );
  const experimentQueriesResults = useQueries({ queries: experimentQueries });

  let loading = false;
  const experimentsById: Record<string, Experiment> = {};
  experimentQueriesResults.forEach((experimentQueryResult) => {
    loading ||= experimentQueryResult.isLoading;
    const experiment = experimentQueryResult.data?.experiment;
    if (experiment) experimentsById[experiment.id] = experiment;
  });
  const registeredModelVersionsWithExperimentsByRegisteredModelId: Record<
    string,
    RegisteredModelVersionWithExperiment[]
  > = {};
  registeredModels.forEach((registeredModel, i) => {
    const versionsWithExperiments: RegisteredModelVersionWithExperiment[] = [];
    registeredModelVersionsWithExperimentsByRegisteredModelId[registeredModel.id] = versionsWithExperiments;
    const modelVersionsQueryResult = modelVersionsQueriesResults[i];
    loading ||= modelVersionsQueryResult.isLoading;
    modelVersionsQueryResult.data?.modelVersions.forEach((version) => {
      const experiment = experimentsById[version.experimentId];
      versionsWithExperiments.push({ ...version, experiment } as RegisteredModelVersionWithExperiment);
    });
  });

  return { loading, registeredModelVersionsWithExperimentsByRegisteredModelId };
};

// Tags

export const useQueryTags = <TData = ListTagsResponse>(
  request: ListTagRequestPlain,
  options?: UseQueryOptions<ListTagsResponse, unknown, TData>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  return useQuery({
    ...options,
    queryFn: ({ signal }) => listTags(mlopsApiUrl, request, { signal }),
    queryKey: cacheKeys.tagsAll(request.projectId, request),
  });
};
